import React, { useState } from "react";
import {
  FaArrowLeft, FaEnvelope, FaEdit, FaMagic, FaSyncAlt, FaDownload, FaFileAlt
} from "react-icons/fa";
import HistoryTab from "./HistoryTab";
import TrackerTable from "./TrackerTable";
import SpeciesEvidencePanel from "../Depositor/SpeciesEvidencePanel";
import { StatusBadge, STATUS_OPTIONS } from "./constants";
import { useHistoryLogger } from "../../contexts/HistoryContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./ProductDetails.css";
import { useAuth } from "../../contexts/AuthContext";
import { fetchHpaEnsemblUrls } from "../../utils/hpaLookup";
import { COLLECTIONS_OPTIONS } from "../../utils/collectionsOptions";

const statusColorMap = {
  "Pending":              { bg: "#fef08a", text: "#854d0e", border: "#fbbf24" },
  "Correction Requested": { bg: "#fee2e2", text: "#7f1d1d", border: "#f87171" },
  "Reviewed":             { bg: "#dbeafe", text: "#0c4a6e", border: "#38bdf8" },
  "Approved":             { bg: "#dcfce7", text: "#15803d", border: "#4ade80" },
};

const syncStatusMap = {
  "synced":       { label: "Synced",       color: "#15803d", bg: "#dcfce7" },
  "not-synced":   { label: "Not Synced",   color: "#6b7280", bg: "#f3f4f6" },
  "sync-failed":  { label: "Sync Failed",  color: "#dc2626", bg: "#fee2e2" },
  "pending-sync": { label: "Pending Sync", color: "#d97706", bg: "#fef3c7" },
};

// Same lists (and clonality-based filtering) as the depositor form — full
// names, not the old abbreviated ones, and Polyclonal only shows serum-type isotypes.
const MONOCLONAL_ISOTOPES = [
  "Armenian Hamster IgG","Goat IgG","Guinea Pig IgG","Human IgG1",
  "Mouse IgA","Mouse IgA, kappa light chain","Mouse IgG","Mouse IgG1",
  "Mouse IgG1, kappa light chain","Mouse IgG1, lambda light chain",
  "Mouse IgG2a","Mouse IgG2a, kappa light chain","Mouse IgG2a, lambda light chain",
  "Mouse IgG2b","Mouse IgG2b, kappa light chain","Mouse IgG2c",
  "Mouse IgG2c, kappa light chain","Mouse IgG3","Mouse IgG3, kappa light chain",
  "Mouse IgG3, lambda light chain","Mouse IgM","Mouse IgM, kappa light chain",
  "Mouse IgM, lambda light chain","Rabbit IgG","Rat IgG","Rat IgG1",
  "Rat IgG1, kappa light chain","Rat IgG2a","Rat IgG2a, kappa light chain",
  "Rat IgG2a, lambda light chain","Rat IgG2b","Rat IgG2b, kappa light chain",
  "Rat IgG2c","Rat IgM","Rat IgM, kappa light chain","Syrian Hamster IgG",
];
const POLYCLONAL_ISOTOPES = [
  "Mouse IgG","Rabbit IgG","Rat IgG","Goat IgG","Guinea Pig IgG",
  "Armenian Hamster IgG","Syrian Hamster IgG",
];

const recommendApplications = [
  "Chromatin Immunoprecipitation","ELISA","FACS","FFPE",
  "Function Blocking","Gel Supershift","Immuno-MRM","Immuno-MS",
  "Immunofluorescence","Immunohistochemistry","Immunoprecipitation",
  "Microarray","Native Western Blot","TRAP","Western Blot",
];

const applicationToPubmedMap = {
  "Initial Characterization": "initial_pubmed_id",
  "Epitope Mapping":          "epitope_map_pubmed_id",
  "Immunohistochemistry":     "ihc_pubmed_id",
  "Immunofluorescence":       "if_pubmed_ids",
  "Western Blot":             "wb_pubmed_id",
  "Immunoprecipitation":      "ip_pubmed_ids",
  "Chromatin Immunoprecipitation": "chip_pubmed_ids",
  "ELISA":                    "elisa_pubmed_ids",
  "FFPE":                     "ffpe_pubmed_ids",
  "Function Blocking":        "fb_pubmed_ids",
  "Microarray":               "microarray_pubmed_ids",
  "TRAP":                     "trap_pubmed_ids",
  "FACS":                     "facs_pubmed_ids",
  "Gel Supershift":           "gel_supershift_pubmed_ids",
  "Other":                    "other_pubmed_ids",
};

// Shared with DepositorForm.js's own Collections picker (see
// src/utils/collectionsOptions.js) - this used to be a separate stale
// placeholder list here that never got the real one, and had drifted:
// it included "Recombinant Antibodies" (an auto-generated, never-pickable
// category per the same rule DepositorForm.js's list follows), two
// collections that don't exist in generator.js's externalIdMap at all
// ("Drosophila antigens", "Epitope tags" - picking either would silently
// vanish from the commerce category export), and was missing 11 valid
// collections the depositor-side list already had.
const collectionsOptions = COLLECTIONS_OPTIONS;


const fmt = (val) => {
  if (val === null || val === undefined || val === "" || val === "0" || val === 0) return "—";
  if (Array.isArray(val)) return val.length ? val.join(", ") : "—";
  return String(val);
};

// Normalizes DB values (1/0, true/false, "1"/"0", "true"/"false") to a JS boolean
const asBool = (val) => val === true || val === 1 || val === "1" || val === "true";

const fmtYesNo = (val) => (asBool(val) ? "Yes" : "No");

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ── Timezone-safe formatter for pure calendar dates (e.g. date_deposited) ──
// Pulls the Y/M/D digits directly out of the string/Date instead of running
// it through toLocaleDateString/toString, which silently convert to the
// browser's local timezone first and can shift the displayed day backward.
const fmtDate = (val) => {
  if (!val) return "—";

  let y, m, d;
  if (typeof val === "string") {
    const match = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return "—";
    [, y, m, d] = match;
    y = Number(y); m = Number(m); d = Number(d);
  } else {
    const dt = new Date(val);
    if (isNaN(dt.getTime())) return "—";
    // Use UTC getters since the server sends DATE columns as UTC midnight
    y = dt.getUTCFullYear(); m = dt.getUTCMonth() + 1; d = dt.getUTCDate();
  }

  return `${MONTH_NAMES[m - 1]} ${d}, ${y}`;
};

// Real timestamps (e.g. submitted_at) DO have time-of-day meaning, so local
// conversion via toLocaleDateString is correct here — kept separate from
// fmtDate on purpose.
const fmtDateTime = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

// Converts a Date object / ISO string / "YYYY-MM-DD" string to "YYYY-MM-DD"
// for <input type="date"> — pulls digits directly, no timezone conversion.
const toDateInputValue = (val) => {
  if (!val) return "";

  if (typeof val === "string") {
    const match = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
    const dt = new Date(val);
    if (isNaN(dt.getTime())) return "";
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
  }

  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
};

function InfoRow({ label, value, link, fullWidth }) {
  const display = fmt(value);
  const isLink  = link && display !== "—";
  return (
    <div className={`pd-row ${fullWidth ? "pd-row--full" : ""}`}>
      <span className="pd-row__label">{label}:</span>
      {isLink
        ? <a className="pd-row__link" href={link} target="_blank" rel="noreferrer">{display}</a>
        : <span className={`pd-row__value ${display === "—" ? "pd-row__value--empty" : ""}`}>{display}</span>
      }
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="pd-section">
      <h4 className="pd-section__title">{title}</h4>
      <div className="pd-section__body">{children}</div>
    </div>
  );
}

function EditField({ label, field, product, updateField, fullWidth, isSelect, options, isTextarea, isDate }) {
  const value = product?.[field] ?? "";
  return (
    <div className={`pd-row ${fullWidth ? "pd-row--full" : ""}`}>
      <span className="pd-row__label">{label}:</span>
      {isSelect ? (
        <select
          className="pd-edit-input"
          value={value}
          onChange={(e) => updateField(field, e.target.value)}
          style={statusColorMap[value] ? {
            backgroundColor: statusColorMap[value].bg,
            color:           statusColorMap[value].text,
            borderColor:     statusColorMap[value].border,
            fontWeight: 600,
          } : {}}
        >
          <option value="">Select…</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : isTextarea ? (
        <textarea
          className="pd-edit-input pd-edit-textarea"
          value={value}
          onChange={(e) => updateField(field, e.target.value)}
        />
      ) : isDate ? (
        <input
          type="date"
          className="pd-edit-input"
          value={toDateInputValue(value)}
          onChange={(e) => updateField(field, e.target.value)}
        />
      ) : (
        <input
          type="text"
          className="pd-edit-input"
          value={value}
          onChange={(e) => updateField(field, e.target.value)}
        />
      )}
    </div>
  );
}

// Yes/No toggle field (renders as a select; backed by a boolean column)
function YesNoField({ label, field, product, updateField, fullWidth }) {
  const value = asBool(product?.[field]) ? "Yes" : "No";
  return (
    <div className={`pd-row ${fullWidth ? "pd-row--full" : ""}`}>
      <span className="pd-row__label">{label}:</span>
      <select
        className="pd-edit-input"
        value={value}
        onChange={(e) => updateField(field, e.target.value === "Yes")}
      >
        <option value="No">No</option>
        <option value="Yes">Yes</option>
      </select>
    </div>
  );
}

// ===============================================
// CollectionsField — fixed dropdown visibility
// ===============================================

// ===============================================
// CollectionsField — v2 fix using fixed positioning
// Bypasses any parent overflow:hidden clipping by computing
// the input's real screen position and rendering the dropdown
// with position:fixed at that coordinate.
// Replace the existing CollectionsField function in ProductDetails.js
// ===============================================

// Replace your existing CollectionsField function with this.
// Fix: use useEffect to keep dropdown position in sync with scroll/resize,
// instead of only calculating on focus/change (which goes stale when scrolled).

function CollectionsField({ product, updateField }) {
  const [search, setSearch]       = useState("");
  const [isOpen, setIsOpen]       = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = React.useRef(null);

  const currentVals = (() => {
    const raw = product?.collections;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter(Boolean);
    return String(raw).split(/,\s*/).map((s) => s.trim()).filter(Boolean);
  })();

  const filteredOptions = collectionsOptions.filter(
    (o) => o.toLowerCase().includes(search.toLowerCase()) && !currentVals.includes(o)
  );

  // Recalculate position whenever the dropdown is open — this runs on
  // every render so scrolling/resizing never leaves it stale.
  const updatePos = React.useCallback(() => {
    if (inputRef.current && isOpen) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, [isOpen]);

  // Keep position in sync with scroll and resize while open
  React.useEffect(() => {
    if (!isOpen) return;
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [isOpen, updatePos]);

  const addCollection = (val) => {
    if (!val || currentVals.includes(val)) return;
    updateField("collections", [...currentVals, val]);
    setSearch("");
  };

  const removeCollection = (val) => {
    updateField("collections", currentVals.filter((c) => c !== val));
  };

  return (
    <div className="pd-row pd-row--full">
      <span className="pd-row__label">Collections:</span>
      <div style={{ flex: 1, position: "relative" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
          {currentVals.length === 0 && (
            <span className="pd-row__value pd-row__value--empty">None selected</span>
          )}
          {currentVals.map((c) => (
            <span key={c} className="pd-app-tag" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              {c}
              <span
                onClick={() => removeCollection(c)}
                style={{ cursor: "pointer", fontWeight: 700, color: "#9ca3af" }}
                title="Remove"
              >×</span>
            </span>
          ))}
        </div>
        <input
          ref={inputRef}
          type="text"
          className="pd-edit-input"
          placeholder="Click to see all collections, or type to search…"
          value={search}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
          onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
        />
      </div>

      {isOpen && (
        <div style={{
          position: "fixed",
          top:   dropdownPos.top,
          left:  dropdownPos.left,
          width: dropdownPos.width,
          border: "1px solid #e5e7eb",
          borderRadius: "6px",
          maxHeight: "220px",
          overflowY: "auto",
          background: "#fff",
          zIndex: 99999,
          boxShadow: "0 10px 25px rgba(0,0,0,0.18)",
        }}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((o) => (
              <div
                key={o}
                onClick={() => addCollection(o)}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                style={{
                  padding: "10px 12px", cursor: "pointer",
                  fontSize: "13px", borderBottom: "1px solid #f3f4f6",
                }}
              >
                {o}
              </div>
            ))
          ) : (
            <div style={{ padding: "10px 12px", fontSize: "12px", color: "#9ca3af" }}>
              {search ? "No matching collections" : "All collections already selected"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AppTag({ label }) {
  return <span className="pd-app-tag">{label}</span>;
}

export default function ProductDetails({
  product,
  pricingRules,
  saveDetails,
  setSelectedProduct,
  onBack,
  products,
  setProducts,
  setEditMode,
  editMode,
  note,
  setNote,
  trackerData,
  setTrackerData,
}) {
  const [activeTab, setActiveTab]             = useState("details");
  const [syncing, setSyncing]                 = useState(false);
  const [showAddTracker, setShowAddTracker]   = useState(false);
  const [showEmailModal, setShowEmailModal]   = useState(false);
  const [emailSubject, setEmailSubject]       = useState("");
  const [emailMessage, setEmailMessage]       = useState("");
  const [originalProduct, setOriginalProduct] = useState(null);
  const [draftProduct, setDraftProduct]       = useState(null);
  const [generatedData, setGeneratedData]     = useState(null);
  // ── Commerce category export — separate file from the main item CSV,
  // this is NetSuite's item-to-commerce-category association table ──
  const [commerceCategoryData, setCommerceCategoryData] = useState(null);
  const [generating, setGenerating]           = useState(false);
  const [selectedSuffixes, setSelectedSuffixes] = useState(["-s", "-f", "-g"]);
  const [showSuffixModal, setShowSuffixModal] = useState(false);
  const { user } = useAuth();
  const { logHistory } = useHistoryLogger();

  const syncStatus = product.syncStatus || "not-synced";
  const { label: syncLabel, color: syncColor, bg: syncBg } = syncStatusMap[syncStatus];
  const displayProduct = editMode ? draftProduct : product;

  const isotypeOptions = displayProduct?.clonality === "Polyclonal"
    ? POLYCLONAL_ISOTOPES
    : displayProduct?.clonality === "Monoclonal"
    ? MONOCLONAL_ISOTOPES
    : [...MONOCLONAL_ISOTOPES];

  const updateProductField = (field, value) => {
    // Clear isotype when clonality changes to a value it's no longer valid for —
    // same behavior as the depositor form.
    if (field === "clonality") {
      const validOptions = value === "Polyclonal" ? POLYCLONAL_ISOTOPES : MONOCLONAL_ISOTOPES;
      const currentIsotype = editMode ? draftProduct?.isotype : product?.isotype;
      if (currentIsotype && !validOptions.includes(currentIsotype)) {
        if (editMode) {
          setDraftProduct((prev) => ({ ...prev, clonality: value, isotype: "" }));
        } else {
          const updated = { ...product, clonality: value, isotype: "" };
          setSelectedProduct(updated);
          setProducts((prev) => prev.map((p) => (p.id === product.id ? updated : p)));
        }
        return;
      }
    }

    if (editMode) {
      setDraftProduct((prev) => ({ ...prev, [field]: value }));
    } else {
      const updated = { ...product, [field]: value };
      setSelectedProduct(updated);
      setProducts((prev) => prev.map((p) => (p.id === product.id ? updated : p)));
    }
  };

  const applicationsArr = (() => {
    const raw = product.applications;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter(Boolean);
    return String(raw).split(/,\s*/).map((s) => s.trim()).filter(Boolean);
  })();

  const collectionsArr = (() => {
    const raw = product.collections;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter(Boolean);
    return String(raw).split(/,\s*/).map((s) => s.trim()).filter(Boolean);
  })();

  // ── Downloads ────────────────────────────────────────────────────
  const downloadProtocolFile = async () => {
    const id = product.submission_id || product.id;
    try {
      const res = await fetch(`https://dshb-deposits.its.uiowa.edu/api/download-protocol-file/${id}`);
      if (!res.ok) { toast.error("No protocol file found"); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = product.protocol_file_name || `protocol_${id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Protocol file downloaded!");
    } catch { toast.error("Failed to download protocol file"); }
  };

  const downloadCSV = async () => {
    try {
      const id  = product.submission_id || product.id;
      const res = await fetch(`https://dshb-deposits.its.uiowa.edu/api/download-submission-csv/${id}`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `submission_${id}_${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded!");
    } catch { toast.error("Failed to download CSV"); }
  };

  // ── Email ────────────────────────────────────────────────────────
  const openEmailModal = () => {
    setEmailSubject(`Regarding ${product.item_name_number || product.id} antibody`);
    setEmailMessage(
      `Hello ${product.depositor_name_contributor},\n\nWe are contacting you regarding the ${product.item_name_number || product.id} antibody.\n\nBest regards,\nDSHB Admin`
    );
    setShowEmailModal(true);
  };

  const sendEmail = async () => {
    if (!product.depositor_email) { toast.error("No email address for this depositor"); return; }
    try {
      const res  = await fetch("https://dshb-deposits.its.uiowa.edu/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: product.depositor_email, subject: emailSubject, message: emailMessage }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(`Failed: ${data.details || data.error}`); return; }
      toast.success("Email sent successfully!");
      logHistory({ antibodyId: product.id, action: `Email sent to ${product.depositor_email}` });
      setShowEmailModal(false);
    } catch (err) { toast.error("Error sending email: " + err.message); }
  };

  // ── Generate ─────────────────────────────────────────────────────
  const handleFillMissing = async (suffixes) => {
    try {
      setGenerating(true);
      const cleanDeposit = {};
      Object.keys(product).forEach((k) => {
        if (k === "protocol_file") return;
        const v = product[k];
        cleanDeposit[k] = Array.isArray(v) ? v.join(", ") : (v !== null && v !== undefined ? String(v) : "");
      });
      const safePricingRules = (pricingRules || []).map((rule) => ({
        suffix:       String(rule.suffix || ""),
        label:        String(rule.label || ""),
        productForm:  String(rule.productForm || ""),
        allowProfit:  Boolean(rule.allowProfit),
        allowHybridoma: Boolean(rule.allowHybridoma),
        npPrices: (rule.npPrices || []).map((t) => ({ price: Number(t.price)||0, qty: Number(t.qty)||0, level: String(t.level||"") })),
        fpPrices: (rule.fpPrices || []).map((t) => ({ price: Number(t.price)||0, qty: Number(t.qty)||0, level: String(t.level||"") })),
      }));

      const hpaAntigenSpecies = (cleanDeposit.antigen_species || "").toLowerCase();
      const hpaGene = (cleanDeposit.gene || "").trim();
      if (hpaAntigenSpecies.includes("human") && hpaGene && hpaGene !== "NaN") {
        const hpaUrls = await fetchHpaEnsemblUrls(hpaGene);
        if (hpaUrls.length) cleanDeposit.human_protein_atlas_override = hpaUrls.join("|");
      }

      const body = JSON.stringify({ deposit: cleanDeposit, pricingRules: safePricingRules, selectedSuffixes: suffixes });

      const previewRes = await fetch("https://dshb-deposits.its.uiowa.edu/api/generate-preview", {
        method: "POST", headers: { "Content-Type": "application/json" }, body,
      });
      if (previewRes.ok) { setGeneratedData(await previewRes.json()); setActiveTab("generate"); }

      const ccPreviewRes = await fetch("https://dshb-deposits.its.uiowa.edu/api/generate-commerce-category-preview", {
        method: "POST", headers: { "Content-Type": "application/json" }, body,
      });
      if (ccPreviewRes.ok) setCommerceCategoryData(await ccPreviewRes.json());

      const response = await fetch("https://dshb-deposits.its.uiowa.edu/api/generate", {
        method: "POST", headers: { "Content-Type": "application/json" }, body,
      });
      if (!response.ok) { toast.error("Generation failed"); return; }

      const blob = await response.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `generated_${product.item_name_number || product.id}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);

      const ccResponse = await fetch("https://dshb-deposits.its.uiowa.edu/api/generate-commerce-category", {
        method: "POST", headers: { "Content-Type": "application/json" }, body,
      });
      // Firefox (and Safari) commonly block a second auto-triggered
      // download fired immediately after the first one, with no user
      // gesture in between -- confirmed live: both files download fine
      // in Chrome, but only the first ever lands in Firefox. A short
      // delay before the second click is the standard workaround.
      await new Promise((resolve) => setTimeout(resolve, 600));
      if (ccResponse.ok) {
        const ccBlob = await ccResponse.blob();
        const ccUrl  = URL.createObjectURL(ccBlob);
        const ccA    = document.createElement("a");
        ccA.href     = ccUrl;
        ccA.download = `commerce_categories_${product.item_name_number || product.id}.csv`;
        document.body.appendChild(ccA); ccA.click(); ccA.remove();
        URL.revokeObjectURL(ccUrl);
      }

      logHistory({ antibodyId: product.id, action: "Generated CSV file" });
      toast.success("Files generated!");
    } catch (err) { toast.error("Error generating file: " + err.message); }
    finally { setGenerating(false); setShowSuffixModal(false); }
  };

  // Re-download just the commerce category CSV without re-running the
  // full generate flow (mirrors downloadCSV/downloadProtocolFile above).
  const downloadCommerceCategoryCSV = async () => {
    try {
      const cleanDeposit = {};
      Object.keys(product).forEach((k) => {
        if (k === "protocol_file") return;
        const v = product[k];
        cleanDeposit[k] = Array.isArray(v) ? v.join(", ") : (v !== null && v !== undefined ? String(v) : "");
      });
      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/generate-commerce-category", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deposit: cleanDeposit }),
      });
      if (!res.ok) { toast.error("Failed to download commerce category CSV"); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `commerce_categories_${product.item_name_number || product.id}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Commerce category CSV downloaded!");
    } catch { toast.error("Failed to download commerce category CSV"); }
  };

  // ── NetSuite Sync ────────────────────────────────────────────────
  // TEMPORARY 2026-10-21 -- PHASE 0 ONLY: calls the read-only connectivity/
  // permissions test (DepositorSyncTest.Suitelet.js) instead of the old
  // 1.5s fake-delay mock this used to be. Doesn't create or edit anything
  // in NetSuite yet -- just proves the browser can reach it and that the
  // deployed role can see Item records.
  //
  // CORRECTED 2026-10-21: calls NetSuite DIRECTLY from the browser, not
  // through the portal's own backend -- that backend server sits behind
  // a restrictive outbound firewall that blocks it entirely (confirmed
  // live: ECONNREFUSED on every attempt). Every other NetSuite
  // integration in this project already works this same
  // browser-to-NetSuite-directly way.
  //
  // Sends the portal's own session token (already sitting in
  // sessionStorage from login) instead of a separate static secret --
  // the Suitelet verifies this token's signature/expiry/admin role
  // itself using the same JWT_SECRET the portal backend signs with.
  // That secret never leaves the server/NetSuite side; only the
  // already-issued, already-expiring token crosses the wire, so there's
  // no new long-lived credential sitting in this public JS bundle for
  // someone to extract and reuse without ever logging in.
  //
  // Replace this with the real create/update Suitelet call once Phase 1
  // is built and tested -- same URL/token pattern, just real write logic
  // on the NetSuite side instead of a lookup.
  const NETSUITE_SYNC_TEST_URL = "https://571578-sb1.extforms.netsuite.com/app/site/hosting/scriptlet.nl?script=1464&deploy=1&compid=571578_SB1&ns-at=AAEJ7tMQxRRadql76raX5mTS6eLjX1iX-ZeV6TKuiHcsZ3eBPfU";

  const handleSyncNetSuite = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const token = sessionStorage.getItem("authToken");
      // CORRECTED 2026-10-21: switched from POST+body to GET+query params --
      // a cross-origin POST (even with a non-preflighted Content-Type) got
      // rejected with a 500 at NetSuite's Akamai edge before ever reaching
      // the Suitelet (confirmed: nothing in its Execution Log for that
      // attempt). Every other working Suitelet call in this project already
      // uses a plain GET with query params, never POST -- matching that
      // proven shape instead of fighting the edge layer.
      const url = `${NETSUITE_SYNC_TEST_URL}&token=${encodeURIComponent(token || "")}&itemName=${encodeURIComponent(product.item_name_number || "")}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Test sync failed");

      const message = data.item
        ? `[TEST] Reached NetSuite — found existing item "${data.item.itemId}" (id ${data.item.internalId})`
        : `[TEST] Reached NetSuite — no existing item named "${product.item_name_number}" (this is expected if it hasn't been created there yet)`;
      const logEntry = { message, date: new Date() };
      const updated = {
        ...product,
        syncStatus: "synced",
        lastSynced: new Date(),
        syncLog: [logEntry, ...(product.syncLog || [])],
      };
      setSelectedProduct(updated);
      setProducts((prev) => prev.map((p) => (p.id === product.id ? updated : p)));
      logHistory({ antibodyId: product.id, action: "Tested NetSuite connection (Phase 0)" });
    } catch (err) {
      const logEntry = { message: `[TEST] Failed: ${err.message}`, date: new Date() };
      setSelectedProduct({ ...product, syncStatus: "sync-failed", syncLog: [logEntry, ...(product.syncLog || [])] });
    } finally { setSyncing(false); }
  };

  // ── Save handler ─────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      // Include admin_note in the update
      const draftWithNote = { ...draftProduct, admin_note: note?.trim() ?? "" };
      //const draftWithNote = (note && note.trim())
      //  ? { ...draftProduct, admin_note: note.trim() }
      //  : draftProduct;

      console.log("🔵 handleSave called");
      console.log("🔵 product.submission_id:", product.submission_id);
      console.log("🔵 draftWithNote.display_in_web_store:", draftWithNote.display_in_web_store);
      console.log("🔵 draftWithNote.allow_sales_for_profit:", draftWithNote.allow_sales_for_profit);
      console.log("🔵 draftWithNote.allow_hybridoma_distribution:", draftWithNote.allow_hybridoma_distribution);
      console.log("🔵 draftWithNote.sequencing_allowed:", draftWithNote.sequencing_allowed);
      console.log("🔵 draftWithNote.date_deposited:", draftWithNote.date_deposited);
      console.log("🔵 draftWithNote.collections:", draftWithNote.collections);
      console.log("🔵 draftWithNote.antibody_registry_id:", draftWithNote.antibody_registry_id);
      console.log("🔵 draftWithNote.admin_note:", draftWithNote.admin_note);
      console.log("🔵 Full payload being sent:", JSON.stringify({
        submission_id: product.submission_id,
        updatedData: draftWithNote
      }, null, 2));

      const res  = await fetch("https://dshb-deposits.its.uiowa.edu/api/update-submission", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ submission_id: product.submission_id, updatedData: draftWithNote }),
      });
      const data = await res.json();

      console.log("🔵 Server response status:", res.status);
      console.log("🔵 Server response data:", data);

      if (!res.ok) { toast.error(data.error || "Update failed"); return; }

      saveDetails(draftWithNote);

      // Log each changed field
      if (originalProduct && draftWithNote) {
        const changedFields = Object.keys(draftWithNote).filter(key =>
          JSON.stringify(originalProduct[key]) !== JSON.stringify(draftWithNote[key])
        );
        for (const field of changedFields) {
          await fetch("https://dshb-deposits.its.uiowa.edu/api/log-history", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              submission_id: product.submission_id,
              admin_name:    user?.firstName ? `${user.firstName} ${user.lastName}` : "Admin",
              action:        field === "admin_note" ? "Admin Note Added" : "Field Updated",
              field_name:    field,
              old_value:     String(originalProduct[field] ?? ""),
              new_value:     String(draftWithNote[field] ?? ""),
            }),
          });
        }
      }

      // UPDATED: was only updating selectedProduct (this one detail view).
      // Bulk Generate and the main product list/table both read from the
      // `products` array instead, which never got this update -- they'd
      // keep showing the pre-edit value (e.g. "allow sales for profit")
      // until a full page refresh re-fetched everything from the server.
      // Matches the setProducts(...) pattern already used by the other
      // handlers in this file (e.g. handleSyncNetSuite above).
      setSelectedProduct(draftWithNote);
      setProducts((prev) => prev.map((p) => (p.id === product.id ? draftWithNote : p)));
      setEditMode(false);
      setDraftProduct(null);
      setNote("");
      toast.success("Updated successfully!");
    } catch (err) { toast.error("Error updating: " + err.message); }
  };

  return (
    <>
      {/* Toasts render via the single global <ToastContainer> in App.js —
          a local one here used to double-fire every toast with mismatched
          styling (App.js's theme="colored" vs this file's unthemed default). */}

      {/* ── Header ── */}
      <div className="pd-header">
        <div className="pd-header__left">
          <button className="pd-back-btn" onClick={() => (onBack ? onBack() : setSelectedProduct(null))}>
            <FaArrowLeft /> Back
          </button>
          <div className="pd-header__title-block">
            <h2 className="pd-header__name">{product.item_name_number || "Antibody Details"}</h2>
            <div className="pd-header__badges">
              {product.isotype && <span className="pd-badge pd-badge--isotype">{product.isotype}</span>}
              <StatusBadge status={product.status} />
              <span className="pd-badge" style={{ background: syncBg, color: syncColor }}>{syncLabel}</span>
            </div>
          </div>
        </div>
        <div className="pd-header__actions">
          <button className="pd-action-btn" onClick={openEmailModal}><FaEnvelope /> Email</button>
          <button className="pd-action-btn" onClick={() => {
            setOriginalProduct({ ...product });
            setDraftProduct({ ...product });
            setNote(product.admin_note || "");
            setEditMode(true);
          }}><FaEdit /> Edit</button>
          <button className="pd-action-btn" onClick={downloadCSV}><FaDownload /> CSV</button>
          <button className="pd-action-btn" onClick={() => setShowSuffixModal(true)}><FaMagic /> Generate</button>
          <button className="pd-action-btn" onClick={handleSyncNetSuite} disabled={syncing}>
            <FaSyncAlt /> {syncing ? "Syncing…" : "Sync"}
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="pd-tabs">
        {["details", "tracker", "generate", "history", "sync"].map((t) => (
          <button
            key={t}
            className={`pd-tab ${activeTab === t ? "pd-tab--active" : ""}`}
            onClick={() => setActiveTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}{t === "sync" ? " Log" : ""}
          </button>
        ))}
      </div>

      {/* ══════════════════════ DETAILS TAB ══════════════════════ */}
      {activeTab === "details" && (
        <div className="pd-details-body">

          {/* Edit toolbar */}
          {editMode && (
            <div className="pd-edit-toolbar">
              <button className="pd-btn pd-btn--save-sm" onClick={handleSave}>Save</button>
              <button className="pd-btn pd-btn--cancel-sm" onClick={() => {
                setEditMode(false);
                setDraftProduct(null);
                setOriginalProduct(null);
                setNote("");
              }}>Cancel</button>
            </div>
          )}

          {/* Admin note textarea (edit mode) */}
          {editMode && (
            <div className="pd-notes-box">
              <label className="pd-notes-label">
                Admin Note{" "}
                <span style={{ fontWeight: 400, color: "#9ca3af", fontSize: "12px" }}>
                  (optional — saved permanently with this record)
                </span>
              </label>
              <textarea
                className="pd-notes-textarea"
                value={note ?? ""}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Fixed isotype based on depositor correction email received 2025-06-01"
              />
            </div>
          )}

          {/* ── 3-col grid ── */}
          <div className="pd-grid-3col">

            <Section title="IDENTITY">
              {editMode ? (
                <>
                  <EditField label="Clone ID / Name"         field="item_name_number"          product={displayProduct} updateField={updateProductField} />
                  <EditField label="Alternate Antibody Name"  field="alternate_name_clone_id"   product={displayProduct} updateField={updateProductField} />
                  <EditField label="Antigen Name"             field="antigen"                   product={displayProduct} updateField={updateProductField} />
                  <EditField label="Alternate Antigen Name"   field="alternative_antigen_names" product={displayProduct} updateField={updateProductField} />
                  <EditField label="Antibody Registry ID"     field="antibody_registry_id"      product={displayProduct} updateField={updateProductField} />
                  <EditField label="Gene "              field="gene"                      product={displayProduct} updateField={updateProductField} />
                  <EditField label="Alternate Gene Names"     field="alternate_gene_names"      product={displayProduct} updateField={updateProductField} />
                  <EditField label="Entrez Gene ID"           field="entrez_gene_id"            product={displayProduct} updateField={updateProductField} />
                  <EditField label="UniProt ID"               field="uniprot_id"                product={displayProduct} updateField={updateProductField} />
                  <EditField label="Antigen Species"          field="antigen_species"           product={displayProduct} updateField={updateProductField} />
                  <EditField label="Antigen Mol. Weight"      field="antigen_molecular_weight"  product={displayProduct} updateField={updateProductField} />
                </>
              ) : (
                <>
                  <InfoRow label="Clone ID / Name"        value={product.item_name_number} />
                  <InfoRow label="Alternate Antibody Name" value={product.alternate_name_clone_id} />
                  <InfoRow label="Antigen Name"            value={product.antigen} />
                  <InfoRow label="Alternate Antigen Name"  value={product.alternative_antigen_names} />
                  <InfoRow label="Antibody Registry ID"    value={product.antibody_registry_id}
                    link={product.antibody_registry_id
                      ? `https://www.antibodyregistry.org/search?q=${encodeURIComponent(product.antibody_registry_id)}` : null} />
                  <InfoRow label="Gene "             value={product.gene} />
                  <InfoRow label="Alternate Gene Names"    value={product.alternate_gene_names} />
                  <InfoRow label="Entrez Gene ID"          value={product.entrez_gene_id}
                    link={product.entrez_gene_id && product.entrez_gene_id !== "0"
                      ? `https://www.ncbi.nlm.nih.gov/gene/${product.entrez_gene_id}` : null} />
                  <InfoRow label="UniProt ID"              value={product.uniprot_id}
                    link={product.uniprot_id ? `https://www.uniprot.org/uniprotkb/${product.uniprot_id}` : null} />
                  <InfoRow label="Antigen Species"         value={product.antigen_species} />
                  <InfoRow label="Antigen Mol. Weight"     value={product.antigen_molecular_weight} />
                  <div className="pd-row pd-row--full">
                    <span className="pd-row__label">Protocol / Reference File:</span>
                    {product.has_protocol_file || product.protocol_file_name ? (
                      <button className="pd-file-btn" onClick={downloadProtocolFile}>
                        <FaFileAlt style={{ marginRight: 5 }} />
                        {product.protocol_file_name || "Download file"}
                      </button>
                    ) : (
                      <span className="pd-row__value pd-row__value--empty">—</span>
                    )}
                  </div>
                </>
              )}
            </Section>

            <Section title="ANTIBODY PROPERTIES">
              {editMode ? (
                <>
                  <EditField label="Clonality"     field="clonality"     product={displayProduct} updateField={updateProductField} isSelect options={["Monoclonal", "Polyclonal"]} />
                  <EditField label="Isotype"        field="isotype"       product={displayProduct} updateField={updateProductField} isSelect options={isotypeOptions} />
                  <EditField label="Host Species"   field="host_species"  product={displayProduct} updateField={updateProductField} />
                  <EditField label="Myeloma Strain" field="myeloma_strain" product={displayProduct} updateField={updateProductField} />
                </>
              ) : (
                <>
                  <InfoRow label="Clonality"     value={product.clonality} />
                  <InfoRow label="Isotype"        value={product.isotype} />
                  <InfoRow label="Host Species"   value={product.host_species} />
                  <InfoRow label="Myeloma Strain" value={product.myeloma_strain} />
                </>
              )}
            </Section>

            <Section title="IMMUNOGEN & EPITOPE">
              {editMode ? (
                <>
                  <EditField label="Immunogen"            field="immunogen"                product={displayProduct} updateField={updateProductField} isTextarea fullWidth />
                  <EditField label="Immunogen Sequence"   field="immunogen_sequence"       product={displayProduct} updateField={updateProductField} isTextarea fullWidth />
                  <EditField label="Epitope Mapped"       field="epitope_mapped"           product={displayProduct} updateField={updateProductField} isSelect options={["Yes", "No"]} />
                  <EditField label="Epitope Location/Seq" field="epitope_location_sequence" product={displayProduct} updateField={updateProductField} isTextarea fullWidth />
                  <EditField label="Additional Information" field="additional_information" product={displayProduct} updateField={updateProductField} isTextarea fullWidth />
                </>
              ) : (
                <>
                  <InfoRow label="Immunogen"            value={product.immunogen}            fullWidth />
                  <InfoRow label="Immunogen Sequence"   value={product.immunogen_sequence}   fullWidth />
                  <InfoRow label="Epitope Mapped"       value={product.epitope_mapped} />
                  <InfoRow label="Epitope Location/Seq" value={product.epitope_location_sequence} fullWidth />
                  <InfoRow label="Additional Information" value={product.additional_information} fullWidth />
                </>
              )}
            </Section>

          </div>

          {/* ── 2-col grid ── */}
          <div className="pd-grid-2col">

            <Section title="SPECIES REACTIVITY">
              {editMode ? (
                <>
                  <EditField label="Confirmed Reactivity" field="positive_tested_species" product={displayProduct} updateField={updateProductField} />
                  <EditField label="Predicted Reactivity" field="predicted_species"       product={displayProduct} updateField={updateProductField} />
                </>
              ) : (
                <>
                  <InfoRow label="Confirmed Reactivity" value={product.positive_tested_species} />
                  <InfoRow label="Predicted Reactivity" value={product.predicted_species} />
                  <div className="pd-row pd-row--full" style={{ marginTop: "8px" }}>
                    <span className="pd-row__label" style={{ display: "block", marginBottom: "8px" }}>Reactivity Evidence:</span>
                    <SpeciesEvidencePanel submissionId={product.submission_id || product.id} />
                  </div>
                </>
              )}
            </Section>

            <Section title="DEPOSITOR">
              {editMode ? (
                <>
                  <EditField label="Depositor(s)"    field="depositor_name_contributor" product={displayProduct} updateField={updateProductField} fullWidth />
                  <EditField label="Institution"     field="depositor_institution"      product={displayProduct} updateField={updateProductField} fullWidth />
                  <EditField label="Email"           field="depositor_email"            product={displayProduct} updateField={updateProductField} fullWidth />
                  <EditField label="Website"         field="depositor_website"          product={displayProduct} updateField={updateProductField} fullWidth />
                  <EditField label="Date Deposited"  field="date_deposited"             product={displayProduct} updateField={updateProductField} isDate fullWidth />
                  <EditField label="Depositor Notes" field="depositor_notes"            product={displayProduct} updateField={updateProductField} isTextarea fullWidth />
                  <EditField label="Status"          field="status"                     product={displayProduct} updateField={updateProductField} isSelect options={["Pending", "Reviewed", "Approved", "Correction Requested"]} fullWidth />
                </>
              ) : (
                <>
                  <InfoRow label="Depositor Name" value={product.depositor_name_contributor} fullWidth />
                  <InfoRow label="Institution"    value={product.depositor_institution}      fullWidth />
                  <InfoRow label="Email"          value={product.depositor_email}
                    link={product.depositor_email ? `mailto:${product.depositor_email}` : null} fullWidth />
                  <InfoRow label="Website"        value={product.depositor_website}
                    link={product.depositor_website || null} fullWidth />
                  <InfoRow label="Date Deposited" value={fmtDate(product.date_deposited)} />
                  <InfoRow label="Submitted On"   value={fmtDateTime(product.submitted_at)} />
                  <div className="pd-row pd-row--full">
                    <span className="pd-row__label">Depositor Notes:</span>
                    {product.depositor_notes
                      ? <span className="pd-depositor-notes">{product.depositor_notes}</span>
                      : <span className="pd-row__value pd-row__value--empty">—</span>}
                  </div>
                </>
              )}
            </Section>

          </div>

          {/* ── Distribution & Visibility ── */}
          <div className="pd-grid-2col">
            <Section title="DISTRIBUTION & VISIBILITY">
              {editMode ? (
                <>
                  <YesNoField label="Display in Web Store"          field="display_in_web_store"          product={displayProduct} updateField={updateProductField} />
                  <YesNoField label="Allow Sales to For-Profits"    field="allow_sales_for_profit"        product={displayProduct} updateField={updateProductField} />
                  <YesNoField label="Allow Hybridoma Distribution"  field="allow_hybridoma_distribution"  product={displayProduct} updateField={updateProductField} />
                  <YesNoField label="Sequencing Allowed"            field="sequencing_allowed"            product={displayProduct} updateField={updateProductField} />
                  <CollectionsField product={displayProduct} updateField={updateProductField} />
                </>
              ) : (
                <>
                  <InfoRow label="Display in Web Store"         value={fmtYesNo(product.display_in_web_store)} />
                  <InfoRow label="Allow Sales to For-Profits"   value={fmtYesNo(product.allow_sales_for_profit)} />
                  <InfoRow label="Allow Hybridoma Distribution" value={fmtYesNo(product.allow_hybridoma_distribution)} />
                  <InfoRow label="Sequencing Allowed"           value={fmtYesNo(product.sequencing_allowed)} />
                  <div className="pd-row pd-row--full">
                    <span className="pd-row__label">Collections:</span>
                    {collectionsArr.length > 0 ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {collectionsArr.map((c) => <AppTag key={c} label={c} />)}
                      </div>
                    ) : (
                      <span className="pd-row__value pd-row__value--empty">—</span>
                    )}
                  </div>
                </>
              )}
            </Section>
          </div>

          {/* ── Recommended Applications ── */}
          <Section title="RECOMMENDED APPLICATIONS">
            {editMode ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                {recommendApplications.map((app) => {
                  const field = applicationToPubmedMap[app];
                  const currentApps = draftProduct?.applications
                    ? (Array.isArray(draftProduct.applications)
                        ? draftProduct.applications
                        : String(draftProduct.applications).split(/,\s*/).filter(Boolean))
                    : [];
                  const isSelected = currentApps.includes(app);
                  return (
                    <div key={app} style={{
                      border: `1.5px solid ${isSelected ? "#3b82f6" : "#e5e7eb"}`,
                      borderRadius: "8px", padding: "10px 12px",
                      background: isSelected ? "#eff6ff" : "#fff",
                      transition: "all 0.15s",
                    }}>
                      <label style={{
                        display: "flex", alignItems: "center", gap: "8px", cursor: "pointer",
                        fontWeight: isSelected ? 600 : 400,
                        color: isSelected ? "#1d4ed8" : "#374151", fontSize: "13px",
                      }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            const updated = isSelected
                              ? currentApps.filter((a) => a !== app)
                              : [...currentApps, app];
                            setDraftProduct((prev) => ({ ...prev, applications: updated }));
                          }}
                        />
                        {app}
                      </label>
                      {isSelected && field && (
                        <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #bfdbfe" }}>
                          <label style={{ fontSize: "11px", color: "#6b7280", display: "block", marginBottom: "4px" }}>
                            PubMed ID(s):
                          </label>
                          <input
                            type="text"
                            className="pd-edit-input"
                            style={{ width: "100%", fontSize: "12px" }}
                            value={draftProduct?.[field] || ""}
                            placeholder="e.g. 12345678, 87654321"
                            onChange={(e) => setDraftProduct((prev) => ({ ...prev, [field]: e.target.value }))}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              applicationsArr.length > 0 ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  {applicationsArr.map((app) => {
                    const field     = applicationToPubmedMap[app];
                    const pubmedVal = field ? product[field] : null;
                    return (
                      <div key={app} style={{
                        border: "1.5px solid #bfdbfe", borderRadius: "8px",
                        padding: "10px 12px", background: "#eff6ff",
                      }}>
                        <span style={{ fontWeight: 600, color: "#1d4ed8", fontSize: "13px" }}>{app}</span>
                        {pubmedVal && fmt(pubmedVal) !== "—" && (
                          <div style={{ marginTop: "6px", paddingTop: "6px", borderTop: "1px solid #bfdbfe" }}>
                            <span style={{ fontSize: "11px", color: "#6b7280" }}>PubMed: </span>
                            <span style={{ fontSize: "12px", color: "#374151" }}>{fmt(pubmedVal)}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="pd-row__value pd-row__value--empty">—</span>
              )
            )}
          </Section>

          {/* ── Admin Note display (view mode only) ── */}
          {!editMode && product.admin_note && (
            <div style={{
              marginTop: "24px",
              padding: "16px 20px",
              background: "#fefce8",
              border: "1px solid #fde047",
              borderLeft: "4px solid #eab308",
              borderRadius: "8px",
              display: "flex",
              gap: "14px",
              alignItems: "flex-start",
            }}>
              <div style={{
                flexShrink: 0,
                width: 32, height: 32,
                background: "#fef9c3",
                border: "1px solid #fde047",
                borderRadius: "6px",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "16px",
              }}>📋</div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: "0 0 6px", fontWeight: 600, fontSize: "13px", color: "#713f12" }}>
                  Admin Note
                </p>
                <p style={{ margin: 0, fontSize: "13px", color: "#78350f", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {product.admin_note}
                </p>
              </div>
            </div>
          )}

        </div>
      )}

      {/* ══════════════════════ TRACKER TAB ══════════════════════ */}
      {activeTab === "tracker" && (
        <div className="pd-tab-panel">
          {trackerData && trackerData.length > 0 ? (
            <TrackerTable
              trackerData={trackerData}
              setTrackerData={setTrackerData}
              products={products}
              setSelectedProduct={() => {}}
              setActiveTab={() => {}}
            />
          ) : (
            <div className="pd-empty-state">
              <p>No tracker data available for this antibody.</p>
              <button className="pd-btn pd-btn--add" onClick={() => setShowAddTracker(true)}>
                + Add Depositor Tracker
              </button>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════ GENERATE TAB ══════════════════════ */}
      {activeTab === "generate" && (
        <div className="pd-tab-panel">
          <div className="pd-gen-header">
            <div>
              <h3 className="pd-gen-title">Generated File Preview</h3>
              <span className="pd-gen-sub">Selected: {selectedSuffixes.join(", ")}</span>
            </div>
            <button className="pd-action-btn" onClick={() => setShowSuffixModal(true)} disabled={generating}>
              <FaMagic /> {generating ? "Generating…" : "Re-Generate"}
            </button>
          </div>
          {!generatedData ? (
            <div className="pd-empty-state">
              <p>Click Generate to preview and download the file.</p>
              <button className="pd-action-btn" onClick={() => setShowSuffixModal(true)}>
                <FaMagic /> Generate
              </button>
            </div>
          ) : (
            <div className="pd-gen-table-wrap">
              <table className="pd-gen-table">
                <thead>
                  <tr>{generatedData.columns.map((c, i) => <th key={i}>{c}</th>)}</tr>
                </thead>
                <tbody>
                  {generatedData.rows.map((row, ri) => (
                    <tr key={ri}>
                      {generatedData.columns.map((c, ci) => (
                        <td key={ci}>
                          {(!row[c] || row[c] === "NaN")
                            ? <span style={{ color: "#d1d5db" }}>—</span>
                            : String(row[c])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Commerce category file — separate from the main item CSV ── */}
          {commerceCategoryData && (
            <>
              <div className="pd-gen-header" style={{ marginTop: "24px" }}>
                <div>
                  <h3 className="pd-gen-title">Commerce Category File Preview</h3>
                  <span className="pd-gen-sub">NetSuite item-to-commerce-category association rows</span>
                </div>
                <button className="pd-action-btn" onClick={downloadCommerceCategoryCSV}>
                  <FaDownload /> Download CSV
                </button>
              </div>
              <div className="pd-gen-table-wrap">
                <table className="pd-gen-table">
                  <thead>
                    <tr>{commerceCategoryData.columns.map((c, i) => <th key={i}>{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {commerceCategoryData.rows.map((row, ri) => (
                      <tr key={ri}>
                        {commerceCategoryData.columns.map((c, ci) => (
                          <td key={ci}>
                            {(!row[c] || row[c] === "NaN")
                              ? <span style={{ color: "#d1d5db" }}>—</span>
                              : String(row[c])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══════════════════════ HISTORY TAB ══════════════════════ */}
      {activeTab === "history" && (
        <div className="pd-tab-panel">
          <HistoryTab recordId={product.submission_id || product.id} />
        </div>
      )}

      {/* ══════════════════════ SYNC LOG TAB ══════════════════════ */}
      {activeTab === "sync" && (
        <div className="pd-tab-panel">
          {(product.syncLog || []).length === 0
            ? <div className="pd-empty-state">No sync activity yet.</div>
            : (product.syncLog || []).map((log, i) => (
              <div key={i} className="pd-sync-entry">
                {log.message} — {new Date(log.date).toLocaleString()}
              </div>
            ))}
        </div>
      )}

      {/* ══════════════════════ SUFFIX MODAL ══════════════════════ */}
      {showSuffixModal && (
        <div className="pd-modal-overlay">
          <div className="pd-modal">
            <h3 className="pd-modal__title">Select Child Item Types</h3>
            <p className="pd-modal__sub">Choose which product forms to include:</p>
            {[
              { suffix: "-s", label: "Supernatant (-s)" },
              { suffix: "-f", label: "Frozen Cells (-f)" },
              { suffix: "-g", label: "Growing Cells (-g)" },
              { suffix: "-x", label: "Purified Antibody (-x)" },
              { suffix: "-p", label: "Polyclonal Sera (-p)" },
            ].map(({ suffix, label }) => (
              <label key={suffix} className={`pd-suffix-opt ${selectedSuffixes.includes(suffix) ? "pd-suffix-opt--active" : ""}`}>
                <input
                  type="checkbox"
                  checked={selectedSuffixes.includes(suffix)}
                  onChange={() => setSelectedSuffixes((prev) =>
                    prev.includes(suffix) ? prev.filter((s) => s !== suffix) : [...prev, suffix]
                  )}
                />
                {label}
              </label>
            ))}
            <div className="pd-modal__actions">
              <button className="pd-btn pd-btn--cancel-sm" onClick={() => setShowSuffixModal(false)}>Cancel</button>
              <button
                className="pd-btn pd-btn--save-sm"
                onClick={() => handleFillMissing(selectedSuffixes)}
                disabled={selectedSuffixes.length === 0 || generating}
              >
                {generating ? "Generating…" : `Generate (${selectedSuffixes.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════ EMAIL MODAL ══════════════════════ */}
      {showEmailModal && (
        <div className="pd-modal-overlay">
          <div className="pd-modal">
            <h3 className="pd-modal__title">Send Email</h3>
            <label className="pd-modal__label">To: <strong>{product.depositor_email}</strong></label>
            <input
              type="text" className="pd-modal__input" placeholder="Subject"
              value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)}
            />
            <textarea
              className="pd-modal__textarea"
              value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)}
            />
            <div className="pd-modal__actions">
              <button className="pd-btn pd-btn--cancel-sm" onClick={() => setShowEmailModal(false)}>Cancel</button>
              <button className="pd-btn pd-btn--save-sm" onClick={sendEmail}>Send</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════ ADD TRACKER MODAL ══════════════════════ */}
      {showAddTracker && (
        <div className="pd-modal-overlay">
          <div className="pd-modal">
            <h3 className="pd-modal__title">Add Tracker Entry</h3>
            <p>Create a new tracker entry for <strong>{product.item_name_number}</strong>?</p>
            <div className="pd-modal__actions">
              <button className="pd-btn pd-btn--cancel-sm" onClick={() => setShowAddTracker(false)}>Cancel</button>
              <button className="pd-btn pd-btn--save-sm" onClick={async () => {
                try {
                  const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/tracker/create", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      submission_id: product.submission_id || product.id,
                      description:   product.item_name_number || product.id,
                      status:        "Pending",
                    }),
                  });
                  if (res.ok) {
                    const { tracker } = await res.json();
                    toast.success("Tracker created!");
                    setShowAddTracker(false);
                    // Switch to Tracker tab — no page reload needed
                    setActiveTab("tracker");
                    if (setTrackerData) {
                      // The Tracker table alone doesn't have the depositor/item
                      // fields TrackerTable displays (those come from a JOIN on
                      // the list endpoint) — fill them in from what we already
                      // have here so the row looks right immediately.
                      const trackerRow = {
                        ...(tracker || {}),
                        submission_id: tracker?.submission_id ?? (product.submission_id || product.id),
                        depositor_name_contributor: product.depositor_name_contributor,
                        depositor_email: product.depositor_email,
                        item_name_number: product.item_name_number,
                      };
                      setTrackerData(prev => Array.isArray(prev) ? [...prev, trackerRow] : [trackerRow]);
                    }
                  } else {
                    toast.error("Failed: " + await res.text());
                  }
                } catch (err) { toast.error("Error: " + err.message); }
              }}>Create Tracker</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}